import DetailView from "@/views/detail/DetailView.vue";

import CloneDetail from "@/views/detail/CloneDetail";
import CloneEndDetail from "@/views/detail/CloneEndDetail";
import EcotypeDetail from "@/views/detail/EcotypeDetail";
import MarkerDetail from "@/views/detail/MarkerDetail";
import HostStrainDetail from "@/views/detail/HostStrainDetail";
import LibraryDetail from "@/views/detail/LibraryDetail";
import LocusDetail from "@/views/detail/LocusDetail";
import GeneDetail from "@/views/detail/GeneDetail";
import GermplasmDetail from "@/views/detail/GermplasmDetail";
import PolymorphismDetail from "@/views/detail/PolymorphismDetail";
import KeywordDetail from "@/views/detail/KeywordDetail";
import PersonDetail from "@/views/detail/PersonDetail";
import OrganizationDetail from "@/views/detail/OrganizationDetail";
import ProteinDetail from "@/views/detail/ProteinDetail";
import ProtocolDetail from "@/views/detail/ProtocolDetail";
import PublicationDetail from "@/views/detail/PublicationDetail";
import TransposonDetail from "@/views/detail/TransposonDetail";
import TransposonFamilyDetail from "@/views/detail/TransposonFamilyDetail";
import VectorDetail from "@/views/detail/VectorDetail";
import SymbolDetail from "@/views/detail/SymbolDetail";
import MicroarrayElementDetail from "@/views/detail/MicroarrayElementDetail";
import SequenceDetail from "@/views/detail/SequenceDetail";
import AnalysisReferenceDetail from "@/views/detail/AnalysisReferenceDetail";
import CommunicationDetail from "@/views/detail/CommunicationDetail";
import store from "../store/store";
import { logTurnaway } from "@/services/LoggingService";

// Access state is populated by fire-and-forget dispatches in App.vue's mounted
// hook, which can run after this guard. Await them so the check reads settled
// state. Both dispatches are self-limiting, so repeat calls are cheap.
const settleAccessState = async () => {
  try {
    await Promise.all([
      store.dispatch("subscriptionStatus/ensureInstitution"),
      // isLoggedIn reads the auth cookies synchronously, so an anonymous
      // visitor makes no auth request at all.
      store.getters["authProfile/isLoggedIn"]
        ? store.dispatch("authProfile/ensureAuthProfile")
        : Promise.resolve(),
    ]);
  } catch (error) {
    // Never wedge navigation; decide on whatever state landed.
    console.error("requireAccess: could not resolve access state", error);
  }
};

const requireAccess = async (to, from, next) => {
  // Enable dev bypass for mock views in non-production builds
  const isDevBuild = process.env.NODE_ENV !== "production";

  // Check for any mock* query parameter or localStorage flag
  const hasMockQuery = Object.keys(to.query).some((key) =>
    key.startsWith("mock")
  );
  const hasMockStorage =
    window.localStorage.getItem("mockPersonDetail") === "1" ||
    window.localStorage.getItem("mockProfile") === "1";
  const devBypass = isDevBuild && (hasMockQuery || hasMockStorage);

  if (devBypass) {
    console.log("Access granted (dev mode)");
    next();
    return;
  }

  // Deliberately not short-circuited on the current userStatus: vuex-persist
  // may have rehydrated it from a previous visit, and trusting that would both
  // deny visitors who have since gained access and admit ones who have lost it.
  await settleAccessState();

  const userStatus = store.getters.userStatus;
  console.log("Full userStatus:", userStatus);

  if (
    (userStatus.isLoggedIn && userStatus.orcidId) ||
    userStatus.subscriptionStatus === "subscribed"
  ) {
    console.log("Access granted");
    next();
  } else {
    console.log("Access denied");
    // TAIR3-873: record the turn-away before redirecting. This visitor is
    // blocked client-side and never reaches the proxy/backend, so without this
    // the hit is missing from the SQS page-view stats.
    logTurnaway(window.location.origin + to.fullPath);
    next("/access-denied");
  }
};

export const detailRoutes = {
  path: "/",
  name: "DetailView",
  component: DetailView,
  children: [
    {
      path: "/clone",
      name: "CloneDetail",
      component: CloneDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/cloneend",
      name: "CloneEndDetail",
      component: CloneEndDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/ecotype",
      name: "EcotypeDetail",
      component: EcotypeDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/locus",
      name: "LocusDetail",
      component: LocusDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/marker",
      name: "MarkerDetail",
      component: MarkerDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/gene",
      name: "GeneDetail",
      component: GeneDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/germplasm",
      name: "GermplasmDetail",
      component: GermplasmDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/hoststrain",
      name: "HostStrainDetail",
      component: HostStrainDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/library",
      name: "LibraryDetail",
      component: LibraryDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/person",
      name: "PersonDetail",
      component: PersonDetail,
    },
    {
      path: "/organization",
      name: "OrganizationDetail",
      component: OrganizationDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/polyallele",
      name: "PolymorphismDetail",
      component: PolymorphismDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/protein",
      name: "ProteinDetail",
      component: ProteinDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/protocol",
      name: "ProtocolDetail",
      component: ProtocolDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/publication",
      name: "PublicationDetail",
      component: PublicationDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/transposon",
      name: "TransposonDetail",
      component: TransposonDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/transposonfamily",
      name: "TransposonFamilyDetail",
      component: TransposonFamilyDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/keyword",
      name: "KeywordDetail",
      component: KeywordDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/vector",
      name: "VectorDetail",
      component: VectorDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/genesymbol",
      name: "SymbolDetail",
      // Free content: no requireAccess guard.
      component: SymbolDetail,
    },
    {
      path: "/sequence",
      name: "SequenceDetail",
      component: SequenceDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/array_element",
      name: "MicroarrayElementDetail",
      component: MicroarrayElementDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/analysisreference",
      name: "AnalysisReferenceDetail",
      component: AnalysisReferenceDetail,
      beforeEnter: requireAccess,
    },
    {
      path: "/communication",
      name: "CommunicationDetail",
      component: CommunicationDetail,
      beforeEnter: requireAccess,
    },
  ],
};
